/**
 * shared/types/games.ts — Games: topic rounds and Would-You-Rather, the Guess Me engine, and Where Am I.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
/* ---- 1b. Game: a back-and-forth topic round (card 5IXdtz7BL3CX) ---- */

/* Would You Rather modes (card DbcAi0izvJln, founder email 2026-10-19): a
 * structured A-or-B game kind beside the free-text topic round — three
 * curated banks, each its own category so keep/discard and the rotation
 * treat a mode the way they treat any other game. */
export type WyrMode = 'wyr-silly' | 'wyr-spicy' | 'wyr-deep';
export const WYR_MODES: WyrMode[] = ['wyr-silly', 'wyr-spicy', 'wyr-deep'];
export function isWyrMode(v: unknown): v is WyrMode {
  return typeof v === 'string' && (WYR_MODES as string[]).includes(v);
}

/** Closed category list — the concierge picks from these, never free-form. */
export type GameCategory = 'places' | 'restaurants' | 'would-you-rather' | 'this-or-that' | 'memory' | WyrMode;

/** Post-round verdict on a game (card OJmoL0w1fGrm). Couple-level per
 * category: a 'discard' drops the category out of the random rotation. */
export type GameRatingVerdict = 'keep' | 'discard';
export type GameRatings = Partial<Record<GameCategory, GameRatingVerdict>>;

export interface GameRound {
  roundId: string;
  category: GameCategory;
  question: string;
  /** 'closed' = abandoned by "next topic" before both answered. */
  state: 'open' | 'revealed' | 'closed';
  createdAt: number;
  revealedAt: number | null;
  /** Who has answered or passed — the text stays hidden until reveal. */
  answeredBy: string[];
  /** This member's own answer, always visible to them. */
  myAnswer: string | null;
  myPassed: boolean;
  /** The partner's answer — null until the round is revealed. */
  partnerAnswer: string | null;
  partnerPassed: boolean;
  /** Couple's post-round verdict on this category, once given (card OJmoL0w1fGrm). */
  rating: GameRatingVerdict | null;
  /** Would-You-Rather options — exactly two strings, null for a topic round
   *  (card DbcAi0izvJln). The answer stored is the option the member picked,
   *  so the reveal and the thread card read as plain text either way. */
  options: [string, string] | null;
  /** Turns completed in the current session (card cNGAFvkiRCGz): a turn is
   *  ONE revealed round; the panel shows the closing card at the limit. */
  turns: number;
}

/** Session length for a game run (card cNGAFvkiRCGz) — 5 revealed rounds,
 *  then the closing card. The counter resets when a fresh round opens. */
export const GAME_TURN_LIMIT = 5;

export interface GameAPI {
  /** The open round, or null when nothing is in play. */
  getGameRound(memberId: string): Promise<GameRound | null>;
  /** Issue a topic. `advance` closes the current round first, so "next topic"
   *  and "start" are one call. An open round is returned unchanged unless
   *  advance is set — both partners must see the same prompt. */
  startGameRound(memberId: string, category?: GameCategory, advance?: boolean): Promise<GameRound>;
  answerGameRound(memberId: string, text: string): Promise<GameRound>;
  passGameRound(memberId: string): Promise<GameRound>;
  /** Record a couple-level verdict on the round's category. Only a revealed
   *  round can be rated; the verdict persists and steers the rotation. */
  rateGameRound(memberId: string, roundId: string, verdict: GameRatingVerdict): Promise<GameRound>;
  /** Every category verdict the couple has given. */
  getGameRatings(): Promise<GameRatings>;
}

/* ---- Guess Me: the two-player round engine (card eq92fFXqQUuP) ---------- */

/** Question families. Curated banks live in the Durable Object — a closed
 *  list like every other game surface, never model output at round-open.
 *  'spicy' is opt-in: it is only ever opened by an explicit pick, never
 *  rotation, never a recommendation. */
export type GuessMeCategory = 'small-stuff' | 'past' | 'dreams' | 'stress-needs' | 'spicy';

export const GUESS_ME_CATEGORIES: GuessMeCategory[] = [
  'small-stuff', 'past', 'dreams', 'stress-needs', 'spicy',
];

export const GUESS_ME_CATEGORY_LABELS: Record<GuessMeCategory, string> = {
  'small-stuff': 'The small stuff',
  past: 'Our past',
  dreams: 'Dreams',
  'stress-needs': 'Stress and needs',
  spicy: 'Spicy',
};

/** The round state machine (card eq92fFXqQUuP): open → A_locked →
 *  B_locked → revealed. A locked submission is immutable; reveal fires
 *  exactly once and every later call reads the stored verdict. */
export type GuessMeState = 'open' | 'A_locked' | 'B_locked' | 'revealed';

/** exact = 2 points, close = 1, miss = 0. */
export type GuessMeVerdict = 'exact' | 'close' | 'miss';

/** Where the verdict came from — 'match' is a mechanical option compare
 *  (the guesser picked one of the three shipped choices), 'ai' is the
 *  free-text judge, 'fallback' is the literal compare that stands in when the
 *  judge is unavailable or unusable (correctable, like 'ai'), 'override' is a
 *  partner overruling it. */
export type GuessMeVerdictSource = 'match' | 'ai' | 'fallback' | 'override';

export const GUESS_ME_SCORE: Record<GuessMeVerdict, number> = {
  exact: 2, close: 1, miss: 0,
};

/** Questions per couple per UTC day (card eq92fFXqQUuP). */
export const GUESS_ME_DAILY_CAP = 3;

export interface GuessMeRound {
  roundId: string;
  /** 1-based; roles swap on parity — odd rounds A answers, even rounds B. */
  index: number;
  category: GuessMeCategory;
  question: string;
  state: GuessMeState;
  createdAt: number;
  revealedAt: number | null;
  /** The CALLING member's role this round. */
  myRole: 'answerer' | 'guesser';
  /** The caller's own submission, always theirs to see. */
  myAnswer: string | null;
  myGuess: string | null;
  /** True once locked — the UI stops offering an edit. */
  myLocked: boolean;
  /** The real answer. Null for the guesser until reveal, always. */
  partnerAnswer: string | null;
  /** The guess the partner made — only shown to the answerer at reveal. */
  partnerGuess: string | null;
  /** The guesser's three choices (real answer + two decoys), shuffled
   *  server-side. Null for the answerer, and null for the guesser until
   *  the decoys are generated. Never identifies which one is real. */
  options: string[] | null;
  verdict: GuessMeVerdict | null;
  verdictSource: GuessMeVerdictSource | null;
  /** True once the answer has actually reached the notebook — the card must
   *  not claim filing it did not do (sol review 2026-10-19). */
  filed: boolean;
  score: number;
  /** The couple's cumulative knowing score in this category. */
  categoryScore: number;
  /** Rounds the couple may still open today. */
  todayLeft: number;
}

export interface GuessMeAPI {
  /** Latest round for this couple, or null.
   *
   *  `now` is a clock seam for the browser walk that proves the daily
   *  allowance returns without a navigation (card qIRcApgTepJd). The worker
   *  forwards it only when PEAR_ENV !== 'prod', and it reaches the READ path
   *  only — every write still judges the cap against the real clock. */
  getGuessMeRound(memberId: string, now?: number): Promise<GuessMeRound | null>;
  /** One named round, if the caller is one of its two players — the read a
   *  retry needs, because "latest" has moved on by then. */
  getGuessMeRoundById(memberId: string, roundId: string): Promise<GuessMeRound | null>;
  /** Newest-first history for the list hook — unrevealed rounds carry only
   *  the caller's own side, exactly as the single-round view does. */
  getGuessMeHistory(memberId: string, limit?: number): Promise<GuessMeRound[]>;
  /** Open a round in a category. Throws 'cap_reached' at the daily cap. */
  startGuessMeRound(memberId: string, category: GuessMeCategory): Promise<GuessMeRound>;
  /** The answerer locks their answer. Immutable from here (card rule).
   *
   *  Every mutation names the round it belongs to. A caller goes stale the
   *  moment the couple moves on, and without the id a delayed answer, decoy
   *  write, guess or overrule lands on whatever round happens to be newest
   *  (sol review finding 1, 2026-10-19). An unknown id throws 'not_found'. */
  lockGuessMeAnswer(memberId: string, roundId: string, text: string): Promise<GuessMeRound>;
  /** Store the two decoys the worker's model produced for the locked
   *  answer. Answerer only, once, before any guess lands. */
  setGuessMeDecoys(memberId: string, roundId: string, decoys: [string, string]): Promise<GuessMeRound>;
  /** The guesser locks a pick — one of the options, or free text. */
  lockGuessMeGuess(memberId: string, roundId: string, text: string): Promise<GuessMeRound>;
  /** Apply a verdict. First call reveals and scores; later calls read back. */
  scoreGuessMeRound(
    memberId: string,
    roundId: string,
    verdict: GuessMeVerdict,
    source: GuessMeVerdictSource,
  ): Promise<GuessMeRound>;
  /** Cumulative knowing score per category. */
  getGuessMeScores(): Promise<Partial<Record<GuessMeCategory, number>>>;
  /** SERVER-INTERNAL. The question and the real answer behind the round that
   *  is waiting on a verdict — the one thing the guesser's own view must
   *  never carry, handed to the worker so it can judge a free-text guess (or
   *  settle an option pick) without the answer ever reaching a client. */
  getGuessMeJudgeContext(roundId: string): Promise<{ question: string; answer: string } | null>;
  /** SERVER-INTERNAL. The single-flight claim on the paid judge, and the
   *  recovery path for a round stranded in `B_locked` (sol review 2026-10-19).
   *  Exactly one caller is told to judge; a claimer that dies releases the
   *  claim when the lease expires and the next request judges the stored
   *  guess. Both sides are already locked, so the outcome is fully determined
   *  by stored data and the answer never has to reach a client. */
  claimGuessMeSettle(memberId: string, roundId: string): Promise<GuessMeSettlement>;
  /** SERVER-INTERNAL, idempotent. File every revealed round that has not
   *  reached the notebook yet — the durable retry behind "a note is written
   *  once, or it is written later". Runs from the room's daily sweep and from
   *  a guess POST that finds its round already settled; returns how many
   *  rounds it filed. */
  retryGuessMeFiling(): Promise<number>;
}

/** What a caller learns when it asks to settle a round. `claimed` is the only
 *  state that may call the model; `busy` means another request holds the
 *  lease; `settled` means the reveal already landed; `none` means this round
 *  is not waiting on a verdict, or is not this member's to settle. */
export interface GuessMeSettlement {
  state: 'claimed' | 'busy' | 'settled' | 'none';
  question?: string;
  answer?: string;
  guess?: string;
  options?: string[] | null;
}

/* ---- Where Am I (card gzUOpwYknx7V / child Y4lreEUYUrLd) ----
 *
 * A SHARED round, not a two-role one: both partners are on the same side,
 * guessing a place the agent picked. Nothing here is sealed, because there is
 * nobody to seal it from — the only secret is the place itself, and it is
 * absent from every view until the round closes.
 */

/** `identified` = a guess matched; `retired` = the couple dropped it. Both are
 *  closed, and both reveal the place — a retire is not a loss, just an exit. */
export type WhereAmIState = 'open' | 'identified' | 'retired';

export interface WhereAmIGuess {
  memberId: string;
  text: string;
  /** True when THIS guess was the one that identified the place. */
  hit: boolean;
  at: number;
}

export interface WhereAmIRound {
  roundId: string;
  /** 1-based, per couple. */
  index: number;
  state: WhereAmIState;
  /** The clues uncovered so far, obscure first. Never contains the answer. */
  clues: string[];
  /** How many clues the bank holds — the ceiling the ladder climbs to. */
  cluesTotal: number;
  /** Still an open round with a clue left to give. */
  canRevealClue: boolean;
  /** Every guess either partner has made, oldest first. */
  guesses: WhereAmIGuess[];
  /** Closed rounds only: the place and the wider region that contains it. */
  place: string | null;
  /** True once this round's idea has actually reached the notebook. */
  filed: boolean;
  region: string | null;
  createdAt: number;
  closedAt: number | null;
}

export interface WhereAmIAPI {
  /** The round in play (or the last one closed today), caller's view. */
  getWhereAmIRound(memberId: string): Promise<WhereAmIRound | null>;
  /** Open a round. Returns the one already in play rather than a second. */
  startWhereAmIRound(memberId: string): Promise<WhereAmIRound>;
  /** One guess, judged against the answer aliases. A miss climbs the ladder. */
  answerWhereAmI(memberId: string, roundId: string, text: string): Promise<WhereAmIRound>;
  /** Uncover the next, more general clue without spending a guess. */
  revealWhereAmIClue(memberId: string, roundId: string): Promise<WhereAmIRound>;
  /** Drop the round. No penalty, no score, place revealed. */
  retireWhereAmIRound(memberId: string, roundId: string): Promise<WhereAmIRound>;
  /** File the identified place as one notebook idea. Idempotent: the round
   *  id is the source event, so a second tap (or a retry after a reload)
   *  returns the idea already written instead of writing another. */
  saveWhereAmIToNotebook(memberId: string, roundId: string): Promise<WhereAmIRound>;
}

/* ---- Half a Map (parent QNCCBnAUtCof / child 0dT0PGnICWW6) ----
 *
 * A CO-OP round with two roles, so the projection is role-aware rather than
 * seat-aware: the Walker's payload has no maze, the Navigator's has no Walker.
 * `walker` and `maze` below are therefore mutually exclusive by construction —
 * a shape that carried both would be the bug this game exists to avoid.
 */

export type HalfAMapState = 'playing' | 'complete';
export type HalfAMapRole = 'walker' | 'navigator';
export type HalfAMapDirection = 'up' | 'down' | 'left' | 'right';

export interface HalfAMapCell {
  row: number;
  col: number;
}

/** One cell the Walker can see, with the walls they can feel from it. */
export interface HalfAMapFogCell extends HalfAMapCell {
  glyph: string;
  walls: { up: boolean; down: boolean; left: boolean; right: boolean };
  /** True only when THIS seen cell is the exit; never a direction to it. */
  exit: boolean;
}

/** The whole maze, for the Navigator. Wall arrays are indexed
 *  `row * size + col`, exactly as `shared/half-a-map.ts` builds them. */
export interface HalfAMapMazeView {
  size: number;
  right: boolean[];
  down: boolean[];
  glyph: string[];
  start: HalfAMapCell;
  exit: HalfAMapCell;
  seed: number;
}

export interface HalfAMapChatLine {
  memberId: string;
  text: string;
  at: number;
}

export interface HalfAMapScoreTerms {
  base: number;
  extraMoves: number;
  messages: number;
  bumps: number;
  locates: number;
  secondsOverPar: number;
}

export interface HalfAMapRoundSummary {
  round: number;
  score: number | null;
  terms: HalfAMapScoreTerms | null;
  moves: number;
  bumps: number;
  locates: number;
  settledAt: number | null;
}

/** One seat's view of the round in play. */
export interface HalfAMapRoundView {
  round: number;
  size: number;
  role: HalfAMapRole;
  /** Shortest walk from start to exit, in moves — the Walker's floor. */
  par: number;
  /** The par clock the `secondsOverPar` term is measured against. */
  parSeconds: number;
  startedAt: number;
  settledAt: number | null;
  score: number | null;
  terms: HalfAMapScoreTerms | null;
  moves: number;
  bumps: number;
  locates: number;
  messages: number;
  /** The Walker: always their own cell. The Navigator: only inside a reveal. */
  walker: HalfAMapCell | null;
  /** A live four-second position reveal (Navigator only). */
  reveal: { row: number; col: number; expiresAt: number } | null;
  /** The fog around the Walker. Empty for the Navigator. */
  fog: HalfAMapFogCell[];
  /** The whole maze. Null for the Walker. */
  maze: HalfAMapMazeView | null;
  chat: HalfAMapChatLine[];
  canMove: boolean;
  canReveal: boolean;
  revealsLeft: number;
  chatLeft: number;
}

export interface HalfAMapMatchView {
  matchId: string;
  /** 1-based, per couple. */
  index: number;
  state: HalfAMapState;
  /** The round in play; stays on the last round once the match is complete. */
  round: number;
  roundCount: number;
  /** Sum of the settled rounds' scores, server-computed. */
  totalScore: number;
  current: HalfAMapRoundView | null;
  history: HalfAMapRoundSummary[];
}

export interface HalfAMapAPI {
  /** The match in play (or the last one finished), caller's view. */
  getHalfAMapMatch(memberId: string): Promise<HalfAMapMatchView | null>;
  /** Open a match. Returns the one already in play rather than a second. */
  startHalfAMapMatch(memberId: string): Promise<HalfAMapMatchView>;
  /** One step. Walker only; the server decides the move, the bump and the score. */
  moveHalfAMapWalker(memberId: string, matchId: string, dir: string): Promise<HalfAMapMatchView>;
  /** Spend one of the two position reveals. Navigator only, four seconds long. */
  revealHalfAMapPosition(memberId: string, matchId: string): Promise<HalfAMapMatchView>;
  /** Match-local chat: 80 characters, 30 lines a round. */
  sendHalfAMapMessage(memberId: string, matchId: string, text: string): Promise<HalfAMapMatchView>;
}

/* ---- Guessmate: the 5-letter duel (card 8gbGPJVD3ch4) -------------------
   A slice on the couple room, not a separate Worker: the two seats are the two
   members already paired, so there is no invite code and no join step.

   PRIVACY CONTRACT (enforced by the projection, not the panel): a match
   payload never carries the partner's secret word before the match settles.
   `partnerWord` is null until then — see GuessmateSlice.view(). */

import type { LetterVerdict } from '../guessmate.ts';

export type GuessmateLetterVerdict = LetterVerdict;
export type GuessmateMode = 'live' | 'async';
export type GuessmateState = 'words' | 'playing' | 'settled';
/** Who won: the seat, or a draw when the two boards finish level. */
export type GuessmateOutcome = 'seat0' | 'seat1' | 'draw';

export interface GuessmateGuess {
  /** 1-based position in that seat's own board. */
  index: number;
  word: string;
  verdicts: GuessmateLetterVerdict[];
  /** Greens on this guess; the seat's score is the sum of these. */
  greens: number;
  at: number;
}

export interface GuessmateMatch {
  matchId: string;
  mode: GuessmateMode;
  state: GuessmateState;
  /** The caller's seat, 0 or 1. */
  mySeat: 0 | 1;
  /** The caller's own secret word, always visible to its author. */
  myWord: string | null;
  /** The partner's secret — null until the match has settled. */
  partnerWord: string | null;
  /** True once this seat has set (and validated) its word. */
  myWordSet: boolean;
  partnerWordSet: boolean;
  myGuesses: GuessmateGuess[];
  /** The partner's board is public: it is their attempt at the caller's word. */
  partnerGuesses: GuessmateGuess[];
  guessesLeft: { mine: number; theirs: number };
  myScore: number;
  partnerScore: number;
  /** Whose turn it is; null once both boards are full. */
  turn: 0 | 1 | null;
  myTurn: boolean;
  /** When the seat on turn runs out of time (null once settled). */
  deadlineAt: number | null;
  outcome: GuessmateOutcome | null;
  createdAt: number;
  settledAt: number | null;
}

export interface GuessmateAPI {
  /** The match in play for this couple, or null. Caller's own projection. */
  getGuessmateMatch(memberId: string): Promise<GuessmateMatch | null>;
  /** Open a match, or return the one already in play. */
  startGuessmateMatch(memberId: string, mode: GuessmateMode): Promise<GuessmateMatch>;
  /** Store this seat's secret word. A word without a passing verdict is
   *  refused — the card's "no game on an unvalidated pair". */
  setGuessmateWord(
    memberId: string,
    matchId: string,
    word: string,
    verdict: { ok: boolean; reason: string | null },
  ): Promise<GuessmateMatch>;
  /** One guess at the partner's word, scored server-side. */
  guessGuessmateWord(memberId: string, matchId: string, word: string): Promise<GuessmateMatch>;
  /** SERVER-INTERNAL: skip turns whose deadline has passed. Returns how many
   *  turns were skipped, so the room's single alarm can report and re-arm. */
  sweepGuessmate(now?: number): Promise<number>;
}
